import { useTranslation } from 'react-i18next';
import { useState, useRef, useEffect, useEffectEvent } from 'react';
import { MessageSquare, X, Loader2, Trash2, Send } from 'lucide-react';
import { photosApi } from '@/api/photos.api';
import type { PhotoComment } from '@/api/photos.api';
import { useToast } from '@/contexts/ToastContext';

interface PhotoCommentsPanelProps {
  photoId: string;
  onClose: () => void;
  onCountChange?: (count: number) => void;
}

export function PhotoCommentsPanel({
  photoId,
  onClose,
  onCountChange,
}: PhotoCommentsPanelProps) {
  const { t } = useTranslation();
  const [comments, setComments] = useState<PhotoComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const { showError } = useToast();

  const reportLoadFailed = useEffectEvent(() => {
    showError(t('screens.photoCommentsPanel.failedToLoadComments'));
  });
  const reportCount = useEffectEvent((count: number) => {
    onCountChange?.(count);
  });

  useEffect(() => {
    setLoading(true);
    setComments([]);
    photosApi
      .getComments(photoId)
      .then(setComments)
      .catch(reportLoadFailed)
      .finally(() => setLoading(false));
  }, [photoId]);

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    reportCount(comments.length);
  }, [comments]);

  const handleSubmit = async () => {
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    try {
      const comment = await photosApi.addComment(photoId, newComment.trim());
      setComments((prev) => [...prev, comment]);
      setNewComment('');
    } catch {
      showError(t('screens.photoCommentsPanel.failedToAddComment'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    setDeletingId(commentId);
    try {
      await photosApi.deleteComment(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch {
      showError(t('screens.photoCommentsPanel.failedToDeleteComment'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="w-80 flex-shrink-0 flex flex-col h-full bg-neutral-800 border-l border-white/10">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-primary-600" />
          <span className="text-white font-medium text-sm">
            {t('screens.photoCommentsPanel.comments')}
          </span>
          {comments.length > 0 && (
            <span className="text-white/40 text-xs">({comments.length})</span>
          )}
        </div>
        <button
          onClick={onClose}
          className="w-6 h-6 rounded-md bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
        >
          <X className="w-3.5 h-3.5 text-white" />
        </button>
      </div>

      {/* Comments list */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 text-white/40 animate-spin" />
          </div>
        ) : comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <MessageSquare className="w-8 h-8 text-white/20 mb-2" />
            <p className="text-white/40 text-sm">
              {t('screens.photoCommentsPanel.noCommentsYet')}
            </p>
            <p className="text-white/25 text-xs mt-1">
              {t('screens.photoCommentsPanel.beTheFirstToComment')}
            </p>
          </div>
        ) : (
          comments.map((comment) => (
            <div
              key={comment.id}
              className="group bg-white/5 rounded-xl px-3 py-2.5"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="text-primary-600 text-xs font-medium truncate max-w-[140px]">
                  {comment.authorName ?? 'Unknown'}
                </span>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-white/30 text-caption">
                    {new Date(comment.createdAt).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <button
                    onClick={() => handleDelete(comment.id)}
                    disabled={deletingId === comment.id}
                    className="pointer-fine:opacity-0 group-hover:opacity-100 w-5 h-5 rounded flex items-center justify-center hover:bg-red-500/20 transition-all"
                    title={t('screens.photoCommentsPanel.deleteComment')}
                  >
                    {deletingId === comment.id ? (
                      <Loader2 className="w-3 h-3 text-red-400 animate-spin" />
                    ) : (
                      <Trash2 className="w-3 h-3 text-red-400" />
                    )}
                  </button>
                </div>
              </div>
              <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap break-words">
                {comment.content}
              </p>
            </div>
          ))
        )}
        <div ref={commentsEndRef} />
      </div>

      {/* Input */}
      <div className="border-t border-white/10 px-3 py-3 flex-shrink-0">
        <div className="flex gap-2 items-end">
          <textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('screens.photoCommentsPanel.addACommentEnterTo')}
            rows={2}
            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder-white/30 resize-none focus:outline-none focus:border-primary-600/50 transition-colors"
          />
          <button
            onClick={handleSubmit}
            disabled={!newComment.trim() || submitting}
            className="w-9 h-9 rounded-xl bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center flex-shrink-0 transition-colors"
            title={t('screens.photoCommentsPanel.sendEnter')}
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 text-white animate-spin" />
            ) : (
              <Send className="w-4 h-4 text-white" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
